(() => {
  'use strict';

  const root = document.querySelector('.aoctant-pdp-atc');

  if (!root) return;


  const targetSelector = '.add-to-cart-button';

  const primarySelector = '.add-to-cart-button';


  const finePointer = window.matchMedia(
    '(hover: hover) and (pointer: fine)'
  );

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );


  const controls = new Set();


  const excludedForegroundSelector = [
    '.add-to-cart__added',
    '[aria-live]',
    '[role="status"]',
    '[data-loading]',
    '[data-status]',
    '.loading',
    '.is-loading',
    '.loader',
    '.spinner',
    '.success',
    '.is-success',
    '.error',
    '.is-error',
  ].join(',');


  const excludedClassPattern =
    /(added|success|checkmark|loading|loader|spinner|status|error)/i;


  const cssVar = (
    control,
    name,
    fallback
  ) =>
    getComputedStyle(control)
      .getPropertyValue(name)
      .trim() || fallback;


  const duration = (control) => {
    const value = cssVar(
      control,
      '--aoctant-button-wave-duration',
      '1000ms'
    );

    return value.endsWith('ms')
      ? parseFloat(value)
      : parseFloat(value) * 1000;
  };


  /* ==========================================================
     NATIVE HORIZON STATE
     ========================================================== */

  const getNativeState = (control) => {
    if (
      control.disabled ||
      control.getAttribute('aria-disabled') === 'true'
    ) {
      return 'disabled';
    }


    if (
      control.getAttribute('aria-busy') === 'true'
    ) {
      return 'processing';
    }


    if (
      control.matches(
        '.loading, .is-loading, .processing, .is-processing'
      )
    ) {
      return 'processing';
    }


    if (
      control.dataset.added === 'true' ||
      control.matches('.success, .is-success')
    ) {
      return 'success';
    }


    if (
      control.matches('.error, .is-error')
    ) {
      return 'error';
    }


    return null;
  };


  const isExcludedForeground = (node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return false;
    }


    if (
      node.matches(excludedForegroundSelector) ||
      node.querySelector(excludedForegroundSelector)
    ) {
      return true;
    }


    return Array.from(node.classList).some(
      (className) =>
        excludedClassPattern.test(className)
    );
  };


  /* ==========================================================
     FOREGROUND CLONES
     ========================================================== */

  const createForegroundLayers = (control) => {
    const sourceNodes = [];


    Array.from(control.childNodes).forEach(
      (node) => {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          node.matches(
            '[data-aoctant-wave-decoration]'
          )
        ) {
          return;
        }


        if (isExcludedForeground(node)) {
          return;
        }


        if (node.nodeType === Node.TEXT_NODE) {
          if (!node.textContent.trim()) {
            return;
          }


          const source =
            document.createElement('span');

          source.className =
            'aoctant-button-wave__source aoctant-button-wave__source-label';


          node.replaceWith(source);

          source.append(node);

          sourceNodes.push(source);

          return;
        }


        if (
          node.nodeType === Node.ELEMENT_NODE
        ) {
          node.classList.add(
            'aoctant-button-wave__source'
          );

          sourceNodes.push(node);
        }
      }
    );


    const createLayer = (modifier) => {
      const layer =
        document.createElement('span');


      layer.className =
        `aoctant-button-wave__foreground aoctant-button-wave__foreground--${modifier}`;


      layer.dataset.aoctantWaveDecoration = '';

      layer.setAttribute(
        'aria-hidden',
        'true'
      );


      sourceNodes.forEach(
        (node) =>
          layer.append(
            node.cloneNode(true)
          )
      );


      return layer;
    };


    return {
      base: createLayer('base'),
      sweep: createLayer('sweep'),
    };
  };


  /* ==========================================================
     LOADING FEEDBACK
     ========================================================== */

  const createLoadingFeedback = () => {
    const loader =
      document.createElement('span');


    loader.className =
      'aoctant-button-wave__feedback aoctant-button-wave__loader';


    loader.dataset.aoctantWaveDecoration = '';

    loader.setAttribute(
      'aria-hidden',
      'true'
    );


    loader.append(
      document.createElement('span'),
      document.createElement('span'),
      document.createElement('span')
    );


    return loader;
  };


  const hasNativeProcessingFeedback =
    (control) =>
      Array.from(
        control.querySelectorAll(
          '.spinner, .loader, [data-loading], [role="status"], [aria-live]'
        )
      ).some(
        (element) =>
          !element.closest(
            '[data-aoctant-wave-decoration]'
          )
      );


  /* ==========================================================
     BIND BUTTON
     ========================================================== */

  const bind = (control) => {
    if (
      control.dataset.aoctantWaveBound ===
      'true'
    ) {
      return;
    }


    control.classList.add(
      'aoctant-button-wave'
    );


    control.classList.add(
      control.matches(primarySelector)
        ? 'aoctant-button-wave--primary'
        : 'aoctant-button-wave--reversed'
    );


    control.dataset.aoctantWaveBound =
      'true';


    control.dataset.aoctantWaveSurface =
      'rest';


    const wave =
      document.createElement('span');


    wave.className =
      'aoctant-button-wave__wave';


    wave.dataset.aoctantWaveDecoration = '';

    wave.setAttribute(
      'aria-hidden',
      'true'
    );


    const foreground =
      createForegroundLayers(control);


    const loadingFeedback =
      createLoadingFeedback();


    control.append(
      wave,
      foreground.base,
      foreground.sweep,
      loadingFeedback
    );


    const state = {
      committed: 'rest',
      desired: 'rest',
      running: false,
      generation: 0,
      nativeOwned: false,

      activeTarget: 'rest',

      waveAnimation: null,
      foregroundAnimation: null,
    };


    /* ========================================================
       CANCEL
       ======================================================== */

    const cancelAnimations = () => {
      state.waveAnimation?.cancel();

      state.foregroundAnimation?.cancel();


      state.waveAnimation = null;

      state.foregroundAnimation = null;
    };


    /* ========================================================
       COMMIT SURFACE
       ======================================================== */

    const commit = (surface) => {
      state.committed = surface;


      control.dataset.aoctantWaveSurface =
        surface;


      wave.style.transform =
        'translate3d(0,var(--aoctant-button-wave-start-y),0)';


      wave.style.background =
        `var(--aoctant-button-wave-${surface}-background)`;


      foreground.sweep.style.clipPath =
        'ellipse(75% 100% at 50% 178%)';


      foreground.sweep.style.color =
        `var(--aoctant-button-wave-${surface}-foreground)`;
    };


    /* ========================================================
       REQUEST SURFACE
       Exact cart-derived transition controller.
       ======================================================== */

    const request = (surface) => {
      state.desired = surface;


      if (state.nativeOwned) {
        return;
      }


      if (state.running) {
        if (
          surface === state.activeTarget
        ) {
          return;
        }


        state.generation += 1;

        cancelAnimations();

        commit(state.activeTarget);

        state.running = false;
      }


      if (
        state.committed === surface
      ) {
        return;
      }


      const generation =
        ++state.generation;


      const startY =
        surface === 'rest'
          ? cssVar(
              control,
              '--aoctant-button-wave-exit-start-y',
              '74%'
            )
          : cssVar(
              control,
              '--aoctant-button-wave-start-y',
              '78%'
            );


      const easing =
        cssVar(
          control,
          '--aoctant-button-wave-ease',
          'cubic-bezier(.22, .75, .2, 1)'
        );


      state.running = true;

      state.activeTarget = surface;


      if (reducedMotion.matches) {
        cancelAnimations();

        commit(surface);

        state.running = false;

        return;
      }


      const timing = {
        duration: duration(control),
        easing,
        fill: 'forwards',
      };


      wave.style.background =
        `var(--aoctant-button-wave-${surface}-background)`;


      foreground.sweep.style.color =
        `var(--aoctant-button-wave-${surface}-foreground)`;


      wave.style.transform =
        `translate3d(0,${startY},0)`;


      foreground.sweep.style.clipPath =
        'ellipse(75% 100% at 50% 178%)';


      /*
       * Force initial visual state before WAAPI begins.
       */

      void wave.offsetWidth;


      state.waveAnimation =
        wave.animate(
          [
            {
              transform:
                `translate3d(0,${startY},0)`,
            },
            {
              transform:
                'translate3d(0,0,0)',
            },
          ],
          timing
        );


      state.foregroundAnimation =
        foreground.sweep.animate(
          [
            {
              clipPath:
                'ellipse(75% 100% at 50% 178%)',
            },
            {
              clipPath:
                'ellipse(75% 100% at 50% 50%)',
            },
          ],
          timing
        );


      state.waveAnimation.finished
        .then(() => {
          if (
            generation !== state.generation
          ) {
            return;
          }


          commit(surface);

          cancelAnimations();


          state.running = false;

          state.activeTarget = surface;
        })
        .catch(() => {
          if (
            generation !== state.generation
          ) {
            return;
          }


          state.waveAnimation = null;

          state.foregroundAnimation = null;

          state.running = false;
        });
    };


    /* ========================================================
       NATIVE STATE SYNCHRONIZATION
       ======================================================== */

    const syncNativeState = () => {
      const nativeState =
        getNativeState(control);


      if (nativeState) {
        state.nativeOwned = true;

        state.generation += 1;

        state.running = false;


        cancelAnimations();

        commit('rest');


        state.desired = 'rest';


        control.dataset.aoctantWaveNativeState =
          nativeState;


        if (
          nativeState === 'processing' &&
          !hasNativeProcessingFeedback(control)
        ) {
          control.dataset.aoctantWaveLoading =
            'true';
        } else {
          control.removeAttribute(
            'data-aoctant-wave-loading'
          );
        }


        return;
      }


      if (!state.nativeOwned) {
        return;
      }


      state.nativeOwned = false;


      control.removeAttribute(
        'data-aoctant-wave-native-state'
      );


      control.removeAttribute(
        'data-aoctant-wave-loading'
      );


      state.desired = 'rest';

      commit('rest');


      request(
        finePointer.matches &&
        control.matches(':hover')
          ? 'hover'
          : 'rest'
      );
    };


    /* ========================================================
       HOVER EVENTS
       ======================================================== */

    control.addEventListener(
      'mouseenter',
      () => {
        if (
          finePointer.matches &&
          !getNativeState(control)
        ) {
          request('hover');
        }
      }
    );


    control.addEventListener(
      'mouseleave',
      () => {
        if (
          finePointer.matches &&
          !getNativeState(control)
        ) {
          request('rest');
        }
      }
    );


    control.aoctantWaveRequest = request;

    control.aoctantWaveSyncNativeState =
      syncNativeState;


    controls.add(control);


    syncNativeState();
  };


  /* ==========================================================
     ROOT BIND
     ========================================================== */

  const bindWithin = (scope) => {
    if (
      scope.nodeType !== Node.ELEMENT_NODE
    ) {
      return;
    }


    if (
      scope.matches(targetSelector)
    ) {
      bind(scope);
    }


    scope
      .querySelectorAll(targetSelector)
      .forEach(bind);
  };


  bindWithin(root);


  /* ==========================================================
     DOM / HORIZON STATE OBSERVER
     ========================================================== */

  const observer =
    new MutationObserver(
      (mutations) => {
        mutations.forEach(
          (mutation) => {
            mutation.addedNodes.forEach(
              bindWithin
            );


            if (
              mutation.type ===
              'attributes'
            ) {
              mutation.target
                .closest?.(
                  targetSelector
                )
                ?.aoctantWaveSyncNativeState?.();
            }
          }
        );
      }
    );


  observer.observe(
    root,
    {
      childList: true,
      subtree: true,
      attributes: true,

      attributeFilter: [
        'disabled',
        'aria-disabled',
        'aria-busy',
        'data-added',
        'class',
      ],
    }
  );


  /* ==========================================================
     RESYNCHRONIZE
     ========================================================== */

  const resynchronize = () => {
    controls.forEach(
      (control) => {
        if (!control.isConnected) {
          controls.delete(control);

          return;
        }


        control
          .aoctantWaveSyncNativeState?.();


        if (
          !getNativeState(control)
        ) {
          control
            .aoctantWaveRequest?.(
              finePointer.matches &&
              control.matches(':hover')
                ? 'hover'
                : 'rest'
            );
        }
      }
    );
  };


  document.addEventListener(
    'visibilitychange',
    () => {
      if (!document.hidden) {
        resynchronize();
      }
    }
  );


  window.addEventListener(
    'blur',
    () => {
      controls.forEach(
        (control) => {
          if (
            !getNativeState(control)
          ) {
            control
              .aoctantWaveRequest?.(
                'rest'
              );
          }
        }
      );
    }
  );


  window.addEventListener(
    'focus',
    resynchronize
  );
})();